"use client";

import Link from "next/link";
import { useCallback, useState } from "react";
import { useSession } from "next-auth/react";
import { usePathname } from "next/navigation";

import { cn } from "~/utils/fontsConfig";
import { NavTitleWrapper } from "./NavTitleWrapper";
import { Card, CardTitle } from "../shadcn/Card";
import { SubNavigationItem } from "./SubNavigationItem";
import { SidebarSection } from "../community-components/CommunitySideBar/SidebarTypes";
import { RouterOutputs } from "~/utils/api";

type Props = {
    section: SidebarSection;
    community?: RouterOutputs["community"]["getSimpleCommunityBySlug"];
};

const SubNavigationGroup = ({ section, community }: Props) => {
    const { data: session } = useSession();
    const pathname = usePathname() ?? "";

    // we use the map to store the state of the menu / submenu. Allows a menu to be persisted irrespective of whether the parent is closed
    // e.g child of parent one is open and parent one is suddenly closed, child remains open (resets to closed on page refresh)
    const [state, setState] = useState<Map<string, boolean>>(new Map());
    const toggle = useCallback((path: string) => {
        setState((prevState) => {
            const newState = new Map(prevState);
            const prevVal = prevState.get(path);

            newState.set(path, !prevVal);
            return newState;
        });
    }, []);

    const path = `parent-section-${section.title}`;
    const isOpen = state.has(path) ? state.get(path) : state.set(path, true);

    const toggleChildren = () => {
        toggle(path);
    };

    const normalizePath = (path?: string) => {
        return path?.replace(/^\/v3/, "");
    };

    const isSelected =
        normalizePath(pathname) === normalizePath(section.titleHref) &&
        // Kinda hack to not highlight the /[communitySlug] which looks like a bug more than a feature
        pathname.split("/").length > 2;

    return (
        <Card className="flex flex-col gap-1 bg-v3-card-lighter-gray px-2 py-2">
            <NavTitleWrapper
                isSelected={isSelected}
                Icon={section.Icon}
                toggleChildren={toggleChildren}
                isOpen={isOpen}
            >
                <CardTitle className="text-lg font-[400]">
                    <span>{section.title}</span>
                </CardTitle>
            </NavTitleWrapper>

            <div
                className={cn(
                    "flex flex-col gap-y-1 overflow-hidden transition-[max-height] duration-300 ease-in-out",
                    {
                        "max-h-0": !isOpen,
                        "max-h-[2000px]": isOpen,
                    },
                )}
            >
                {section.items
                    .filter(
                        (item) =>
                            !item.showForAdmin ||
                            session?.user?.role === "ADMIN",
                    )
                    .map((item) => {
                        return (
                            <SubNavigationItem
                                community={community}
                                key={item.name}
                                sideBarItem={item}
                                depth={1}
                                state={state}
                                toggle={toggle}
                            />
                        );
                    })}
            </div>
        </Card>
    );
};

export default SubNavigationGroup;
